import { useTranslation } from "react-i18next";
import { ShieldCheck, QrCode, AddressBook, MagnifyingGlass, Check, X, SealCheck } from "@phosphor-icons/react";
import SEOHead from "../components/SEOHead";

const ScannerHeader = ({ icon: Icon, badge, title, subtitle, accentColor }) => {
  const colors = {
    emerald: { border: "border-emerald-500/30", bg: "bg-emerald-500/10", text: "text-emerald-400", glow: "rgba(52,211,153,0.4)" },
    amber: { border: "border-amber-500/30", bg: "bg-amber-500/10", text: "text-amber-400", glow: "rgba(245,158,11,0.4)" },
    red: { border: "border-red-500/30", bg: "bg-red-500/10", text: "text-red-400", glow: "rgba(239,68,68,0.4)" },
    violet: { border: "border-violet-500/30", bg: "bg-violet-500/10", text: "text-violet-400", glow: "rgba(139,92,246,0.4)" },
  };
  const c = colors[accentColor];
  return (
    <div className="flex items-start gap-4 mb-6">
      <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl ${c.bg} border ${c.border} flex items-center justify-center flex-shrink-0`}>
        <Icon size={28} weight="duotone" className={c.text} style={{ filter: `drop-shadow(0 0 8px ${c.glow})` }} />
      </div>
      <div>
        <span className={`${c.text} text-xs font-bold tracking-widest uppercase`}>{badge}</span>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight">{title}</h2>
        <p className="text-gray-400 text-base md:text-lg italic mt-2">{subtitle}</p>
      </div>
    </div>
  );
};

const Prose = ({ children }) => <div className="text-gray-300 text-base leading-relaxed space-y-4">{children}</div>;
const H3 = ({ children }) => <h3 className="text-xl md:text-2xl font-bold text-white mt-10 mb-4">{children}</h3>;
const Note = ({ children }) => <p className="text-gray-500 text-sm italic mt-4">{children}</p>;

const BulletList = ({ items }) => (
  <ul className="space-y-3 my-4">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3">
        <Check size={16} weight="bold" className="text-violet-400 flex-shrink-0 mt-1" />
        <span className="text-gray-300 text-sm md:text-base leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);

const XList = ({ items }) => (
  <ul className="space-y-3 my-4">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3">
        <X size={16} weight="bold" className="text-gray-600 flex-shrink-0 mt-1" />
        <span className="text-gray-500 text-sm md:text-base leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);

const ResultTable = ({ rows }) => (
  <div className="overflow-x-auto my-6">
    <table className="w-full text-left border-collapse">
      <thead><tr className="border-b border-white/10">
        <th className="py-3 px-4 text-white font-semibold text-sm">Result</th>
        <th className="py-3 px-4 text-white font-semibold text-sm">Level</th>
        <th className="py-3 px-4 text-white font-semibold text-sm">Meaning</th>
      </tr></thead>
      <tbody>{rows.map((row) => (
        <tr key={`${row.label}-${row.level}`} className="border-b border-white/5 hover:bg-white/[0.02]">
          <td className="py-3 px-4 font-semibold text-sm" style={{ color: row.color }}>{row.dot} {row.label}</td>
          <td className="py-3 px-4 text-sm" style={{ color: row.color }}>{row.level}</td>
          <td className="py-3 px-4 text-gray-400 text-sm">{row.meaning}</td>
        </tr>
      ))}</tbody>
    </table>
  </div>
);

const Card = ({ title, children }) => (
  <div className="p-5 bg-white/[0.03] border border-white/[0.06] rounded-2xl my-4">
    <h4 className="text-white font-bold mb-2">{title}</h4>
    <div className="text-gray-400 text-sm leading-relaxed">{children}</div>
  </div>
);

const SCANNER2_RESULT_KEYS = [
  { dot: "🟢", label: "Secured", level: "Green", color: "#4ade80", i18nKey: "securityPage.scanner2.resultGreen" },
  { dot: "🟡", label: "Warning", level: "Yellow", color: "#facc15", i18nKey: "securityPage.scanner2.resultYellow" },
  { dot: "🔴", label: "Danger", level: "Red", color: "#f87171", i18nKey: "securityPage.scanner2.resultRed" },
];

const SCANNER3_RESULT_KEYS = [
  { dot: "🟢", label: "Safe", level: "Green", color: "#4ade80", i18nKey: "securityPage.scanner3.riskGreen" },
  { dot: "🟢", label: "Low", level: "Green", color: "#4ade80", i18nKey: "securityPage.scanner3.riskLow" },
  { dot: "🟡", label: "Medium", level: "Yellow", color: "#facc15", i18nKey: "securityPage.scanner3.riskYellow" },
  { dot: "🟠", label: "High", level: "Orange", color: "#fb923c", i18nKey: "securityPage.scanner3.riskOrange" },
  { dot: "🔴", label: "Critical", level: "Red", color: "#f87171", i18nKey: "securityPage.scanner3.riskRed" },
];

const SecurityPage = () => {
  const { t } = useTranslation();
  const scanner2Rows = SCANNER2_RESULT_KEYS.map((r) => ({ ...r, meaning: t(r.i18nKey) }));
  const scanner3Rows = SCANNER3_RESULT_KEYS.map((r) => ({ ...r, meaning: t(r.i18nKey) }));
  return (
    <main role="main" className="min-h-screen bg-lumina-dark pt-24 overflow-x-hidden">
      <SEOHead
        title={t("securityPage.seo.title")}
        description={t("securityPage.seo.description")}
        path="/securite"
        pathByLang={{
          fr: "/securite",
          en: "/security",
          es: "/securite",
          de: "/securite",
          it: "/securite",
          pt: "/securite",
          ru: "/securite",
          zh: "/securite",
          ar: "/securite",
          ja: "/securite",
        }}
      />
      <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-lumina-violet/[0.06] rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-purple-600/[0.04] rounded-full blur-3xl" />
      </div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* HERO */}
        <section className="py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-violet-400/40 bg-violet-500/[0.1] mb-8" style={{ boxShadow: '0 0 20px rgba(139,92,246,0.2)' }}>
            <ShieldCheck size={20} weight="duotone" className="text-violet-400" style={{ filter: 'drop-shadow(0 0 8px rgba(139,92,246,0.8))' }} />
            <span className="text-violet-300 text-sm sm:text-base font-bold tracking-[0.12em] uppercase" style={{ textShadow: '0 0 15px rgba(139,92,246,0.6)' }}>{t("securityPage.badge")}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[0.9] tracking-tight text-white mb-6">
            {t("securityPage.hero.title1")} <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #a855f7 0%, #9333ea 40%, #e879f9 100%)' }}>{t("securityPage.hero.title2")}</span>
          </h1>
          <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">{t("securityPage.hero.subtitle")}</p>
        </section>

        {/* INTRO */}
        <section className="pb-12 border-b border-white/[0.06]">
          <Prose><p>{t("securityPage.intro.p1")}</p><p>{t("securityPage.intro.p2")}</p></Prose>
        </section>

        {/* SCANNER 1 */}
        <section className="py-16 md:py-24">
          <ScannerHeader icon={QrCode} badge={t("securityPage.scanner1.badge")} title={t("securityPage.scanner1.title")} subtitle={t("securityPage.scanner1.subtitle")} accentColor="emerald" />
          <Prose>
            <p>{t("securityPage.scanner1.p1")}</p>
            <Card title={t("securityPage.scanner1.sendTitle")}><p>{t("securityPage.scanner1.sendP1")}</p></Card>
            <Card title={t("securityPage.scanner1.wcTitle")}><p>{t("securityPage.scanner1.wcP1")}</p></Card>
            <H3>{t("securityPage.scanner1.whyTitle")}</H3>
            <p>{t("securityPage.scanner1.whyP1")}</p>
            <H3>{t("securityPage.scanner1.formatsTitle")}</H3>
            <p>{t("securityPage.scanner1.formatsIntro")}</p>
            <BulletList items={t("securityPage.scanner1.formats", { returnObjects: true })} />
          </Prose>
        </section>

        <div className="border-b border-white/[0.06]" />

        {/* SCANNER 2 */}
        <section className="py-16 md:py-24">
          <ScannerHeader icon={AddressBook} badge={t("securityPage.scanner2.badge")} title={t("securityPage.scanner2.title")} subtitle={t("securityPage.scanner2.subtitle")} accentColor="amber" />
          <Prose>
            <p>{t("securityPage.scanner2.intro")}</p>
            <p>{t("securityPage.scanner2.stagesIntro")}</p>
            <H3>{t("securityPage.scanner2.stage1Title")}</H3>
            <Note>{t("securityPage.scanner2.stage1Subtitle")}</Note>
            <p>{t("securityPage.scanner2.stage1Intro")}</p>
            <BulletList items={t("securityPage.scanner2.stage1Checks", { returnObjects: true })} />
            <H3>{t("securityPage.scanner2.stage2Title")}</H3>
            <Note>{t("securityPage.scanner2.stage2Subtitle")}</Note>
            <p>{t("securityPage.scanner2.stage2P1")}</p>
            <BulletList items={t("securityPage.scanner2.stage2Threats", { returnObjects: true })} />
            <p>{t("securityPage.scanner2.stage2P2")}</p>
            <H3>{t("securityPage.scanner2.resultTitle")}</H3>
            <ResultTable rows={scanner2Rows} />
            <p className="text-white font-semibold">{t("securityPage.scanner2.resultNote")}</p>
          </Prose>
        </section>

        <div className="border-b border-white/[0.06]" />

        {/* SCANNER 3 */}
        <section className="py-16 md:py-24">
          <ScannerHeader icon={MagnifyingGlass} badge={t("securityPage.scanner3.badge")} title={t("securityPage.scanner3.title")} subtitle={t("securityPage.scanner3.subtitle")} accentColor="red" />
          <Prose>
            <H3>{t("securityPage.scanner3.problemTitle")}</H3>
            <p>{t("securityPage.scanner3.problemP1")}</p>
            <div className="my-4 p-4 bg-white/[0.03] border border-white/[0.08] rounded-xl font-mono text-xs text-gray-500 break-all">0x095ea7b3000000000000000000000000...</div>
            <p>{t("securityPage.scanner3.problemP2")}</p>
            <H3>{t("securityPage.scanner3.solutionTitle")}</H3>
            <p>{t("securityPage.scanner3.solutionP1")}</p>
            <Card title={t("securityPage.scanner3.erc20Title")}>
              <p className="mb-3">{t("securityPage.scanner3.erc20Intro")}</p>
              <BulletList items={t("securityPage.scanner3.erc20Points", { returnObjects: true })} />
            </Card>
            <Card title={t("securityPage.scanner3.permitTitle")}><p>{t("securityPage.scanner3.permitP1")}</p></Card>
            <Card title={t("securityPage.scanner3.nativeTitle")}><p>{t("securityPage.scanner3.nativeP1")}</p></Card>
            <Card title={t("securityPage.scanner3.dexTitle")}><p>{t("securityPage.scanner3.dexP1")}</p></Card>
            <Card title={t("securityPage.scanner3.messageTitle")}><p>{t("securityPage.scanner3.messageP1")}</p></Card>
            <H3>{t("securityPage.scanner3.riskTitle")}</H3>
            <ResultTable rows={scanner3Rows} />
            <Note>{t("securityPage.scanner3.riskNote")}</Note>
          </Prose>
        </section>

        <div className="border-b border-white/[0.06]" />

        {/* BONUS: FAKE TOKEN PROTECTION */}
        <section className="py-16 md:py-24">
          <ScannerHeader
            icon={SealCheck}
            badge={t("securityPage.fakeTokens.badge")}
            title={t("securityPage.fakeTokens.title")}
            subtitle={t("securityPage.fakeTokens.subtitle")}
            accentColor="violet"
          />
          <Prose>
            <H3>{t("securityPage.fakeTokens.problemTitle")}</H3>
            <p>{t("securityPage.fakeTokens.problemP1")}</p>
            <p>{t("securityPage.fakeTokens.problemP2")}</p>
            <H3>{t("securityPage.fakeTokens.solutionTitle")}</H3>
            <p>{t("securityPage.fakeTokens.solutionP1")}</p>
            <BulletList items={t("securityPage.fakeTokens.solutionPoints", { returnObjects: true })} />
            <H3>{t("securityPage.fakeTokens.resultTitle")}</H3>
            <Card title={t("securityPage.fakeTokens.example1Title")}>
              <p>{t("securityPage.fakeTokens.example1P1")}</p>
            </Card>
            <Card title={t("securityPage.fakeTokens.example2Title")}>
              <p>{t("securityPage.fakeTokens.example2P1")}</p>
            </Card>
            <Note>{t("securityPage.fakeTokens.finalNote")}</Note>
          </Prose>
        </section>

        <div className="border-b border-white/[0.06]" />

        {/* WHAT WE DO / DON'T */}
        <section className="py-16 md:py-24">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-8">{t("securityPage.whatWeDo.title")}</h2>
          <Prose>
            <H3>{t("securityPage.whatWeDo.doesTitle")}</H3>
            <BulletList items={t("securityPage.whatWeDo.doesList", { returnObjects: true })} />
            <H3>{t("securityPage.whatWeDo.doesntTitle")}</H3>
            <p>{t("securityPage.whatWeDo.doesntP1")}</p>
            <p>{t("securityPage.whatWeDo.doesntP2")}</p>
            <p className="text-white font-semibold">{t("securityPage.whatWeDo.doesntP3")}</p>
          </Prose>
        </section>

        <div className="border-b border-white/[0.06]" />

        {/* CONCLUSION */}
        <section className="py-16 md:py-24 text-center">
          <ShieldCheck size={48} weight="duotone" className="text-violet-400 mx-auto mb-6" style={{ filter: 'drop-shadow(0 0 12px rgba(139,92,246,0.6))' }} />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-6">{t("securityPage.conclusion.title")}</h2>
          <Prose>
            <p className="text-center">{t("securityPage.conclusion.p1")}</p>
          </Prose>
          <div className="max-w-md mx-auto mt-6">
            <BulletList items={t("securityPage.conclusion.vectors", { returnObjects: true })} />
          </div>
          <p className="text-white font-black text-xl md:text-2xl mt-10" style={{ textShadow: '0 0 20px rgba(139,92,246,0.3)' }}>{t("securityPage.conclusion.tagline")}</p>
          <p className="text-gray-400 text-lg mt-3">{t("securityPage.conclusion.closing")}</p>
        </section>
      </div>
    </main>
  );
};

export default SecurityPage;
